// src/NewGame.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Copy } from "lucide-react";
import { supabase } from "./supabaseClient";
import { useAuth } from "./AuthContext";
import UploadBuild from "./UploadBuild";

// Snippets devs can copy for ready-signal
const PLAYSET_READY_SNIPPETS = {
  godot: `# Godot 4.x (Web export)
func _ready() -> void:
    if OS.get_name() == "Web" and Engine.has_singleton("JavaScriptBridge"):
        JavaScriptBridge.eval(
            "if (window.playsetGameReady) window.playsetGameReady();",
            false
        )
`,
  unity: `// Unity (WebGL)
//
// 1. Put this in a C# script on a GameObject that exists when your game is ready.
// 2. Make sure the script is included in the WebGL build.

using System.Runtime.InteropServices;
using UnityEngine;

public class PlaysetReady : MonoBehaviour
{
    #if UNITY_WEBGL && !UNITY_EDITOR
    [DllImport("__Internal")]
    private static extern void playsetGameReady();
    #endif

    void Start()
    {
        #if UNITY_WEBGL && !UNITY_EDITOR
        try { playsetGameReady(); } catch {}
        #endif
    }
}
`,
  js: `// Plain JavaScript / HTML5
// Call this once your first playable scene is ready.

if (window.playsetGameReady) {
  window.playsetGameReady();
}

// Example: after your assets are loaded
// loader.onComplete = () => {
//   startGame();
//   if (window.playsetGameReady) window.playsetGameReady();
// };
`,
};

export default function NewGame() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [developerName, setDeveloperName] = useState("");
  const [requiresReadySignal, setRequiresReadySignal] = useState(false);
  const [game, setGame] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [snippetCopied, setSnippetCopied] = useState(null);

  if (!currentUser) {
    // ProtectedRoute should prevent this, but just in case
    return (
      <div className="flex-1 bg-[#111] text-[#eee] font-nunito p-8">
        You need to be signed in to create a game.
      </div>
    );
  }

  const handleCopySnippet = async (key) => {
    const text = PLAYSET_READY_SNIPPETS[key];
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setSnippetCopied(key);
      setTimeout(() => setSnippetCopied(null), 1500);
    } catch (e) {
      console.error("Failed to copy snippet:", e);
    }
  };

  const handleCreateGame = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError("Please enter a game name.");
      return;
    }

    setSaving(true);
    setError("");

    try {
      // use the RPC instead of direct insert
      const { data, error } = await supabase.rpc("create_game_with_limit", {
        p_owner_id: currentUser.id,
        p_name: name.trim(),
        p_developer_name: developerName.trim() || null,
        p_require_ready: requiresReadySignal,
      });

      if (error) {
        console.error("Failed to create game via RPC:", error);

        if (
          typeof error.message === "string" &&
          error.message.toLowerCase().includes("maximum number of games")
        ) {
          setError(
            "You've reached the maximum number of games for your plan. Please delete an existing game, or upgrade your plan for more."
          );
        } else if (
          typeof error.message === "string" &&
          error.message.toLowerCase().includes("subscription has expired")
        ) {
          setError(
            "Your subscription has expired. Please renew it to create or access games."
          );
        } else {
          setError("Could not create game. Please try again.");
        }
        return;
      }

      const newGame = Array.isArray(data) ? data[0] : data;
      setGame(newGame);
    } catch (err) {
      console.error("Failed to create game (unexpected):", err);
      setError("Could not create game. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleInitialBuildUploaded = (build) => {
    if (!build || !game) return;
    // Go straight to that game's dashboard
    navigate(`/dashboard?game=${game.id}`);
  };

  return (
    <div className="flex-1 bg-[#111] text-[#eee] font-nunito">
      <div className="max-w-3xl mr-auto space-y-8">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Create a new game
          </h1>
          <p className="mt-2 text-sm text-[#aaa]">
            Set up your game and upload the first build for playtesting.
          </p>
        </div>

        {/* Step 1: Game details */}
        <section className="bg-[#151515] border border-[#252525] rounded-xl p-5 space-y-4">
          <h2 className="text-lg font-semibold">Step 1 — Game details</h2>
          <form onSubmit={handleCreateGame} className="space-y-4">
            <div>
              <label className="block text-sm mb-1">Game name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full rounded-lg bg-[#222] border border-[#333] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#8338ec]"
                placeholder="e.g. Piano Dash"
              />
            </div>

            <div>
              <div className="flex flex-row gap-x-4 items-baseline mb-1">
                <label className="block text-sm">Developer name</label>
                <p className="text-xs text-[#fff]/80">
                  Shown to testers during playtests.
                </p>
              </div>
              <input
                type="text"
                value={developerName}
                onChange={(e) => setDeveloperName(e.target.value)}
                className="w-full rounded-lg bg-[#222] border border-[#333] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#8338ec]"
                placeholder="e.g. Playset Games"
              />
            </div>

            {/* Ready signal opt-in – matches DevDashboard styling */}
            <div className="mt-2 border-t border-[#222] pt-4">
              <label className="flex items-start gap-3 text-sm text-[#aaa]">
                <input
                  type="checkbox"
                  checked={requiresReadySignal}
                  onChange={(e) => setRequiresReadySignal(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-[#444] bg-[#222] accent-[#8338ec]"
                />
                <span>
                  <div className="font-semibold text-xs text-[#1f1f1f] bg-yellow-400 px-1 rounded w-fit mb-2">
                    Recommended!
                  </div>
                  <div className="font-semibold mb-1">
                    Only start session &amp; video recordings after my game
                    signals it&apos;s ready.
                  </div>
                  <span className="text-[#888] text-xs sm:text-sm">
                    When enabled, Playset waits for your build to call{" "}
                    <code className="px-1 py-0.5 rounded bg-black/40 border border-[#333] text-[10px]">
                      window.playsetGameReady()
                    </code>{" "}
                    before starting recordings. Otherwise, recordings may
                    include your loading screen and download time, and you&apos;ll
                    consume this as part of your monthly playtest session time
                    allowance.
                  </span>
                </span>
              </label>

              {requiresReadySignal && (
                <div className="mt-3 space-y-2">
                  <div className="text-xs sm:text-sm text-[#aaa]">
                    Add one of these snippets to your first playable screen:
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    {/* Godot */}
                    <div className="border border-[#333] rounded-lg p-2 bg-black/30 flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[11px] font-semibold text-[#eee]">
                          Godot (Web export)
                        </span>
                        <button
                          type="button"
                          onClick={() => handleCopySnippet("godot")}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-[#444] text-[10px] text-[#ddd] hover:border-[#38bdf8] hover:text-[#38bdf8]"
                        >
                          <Copy className="w-3 h-3" />
                          {snippetCopied === "godot" ? "Copied!" : "Copy"}
                        </button>
                      </div>
                      <img
                        src="/godot_logo.svg"
                        alt="Godot logo"
                        className="h-12 object-contain mx-auto mt-2"
                      />
                      {/* If you ever want to show the raw snippet:
                      <pre className="text-[10px] whitespace-pre-wrap bg-[#050505] border border-[#222] rounded px-2 py-1 overflow-x-auto">
                        {PLAYSET_READY_SNIPPETS.godot}
                      </pre>
                      */}
                    </div>

                    {/* Unity */}
                    <div className="border border-[#333] rounded-lg p-2 bg-black/30 flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[11px] font-semibold text-[#eee]">
                          Unity (WebGL)
                        </span>
                        <button
                          type="button"
                          onClick={() => handleCopySnippet("unity")}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-[#444] text-[10px] text-[#ddd] hover:border-[#38bdf8] hover:text-[#38bdf8]"
                        >
                          <Copy className="w-3 h-3" />
                          {snippetCopied === "unity" ? "Copied!" : "Copy"}
                        </button>
                      </div>
                      <img
                        src="/unity_logo.svg"
                        alt="Unity logo"
                        className="h-12 object-contain invert mx-auto mt-2"
                      />
                      {/* <pre className="text-[10px] whitespace-pre-wrap bg-[#050505] border border-[#222] rounded px-2 py-1 overflow-x-auto">
                        {PLAYSET_READY_SNIPPETS.unity}
                      </pre> */}
                    </div>

                    {/* Plain JS */}
                    <div className="border border-[#333] rounded-lg p-2 bg-black/30 flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[11px] font-semibold text-[#eee]">
                          Plain JavaScript / HTML5
                        </span>
                        <button
                          type="button"
                          onClick={() => handleCopySnippet("js")}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-[#444] text-[10px] text-[#ddd] hover:border-[#38bdf8] hover:text-[#38bdf8]"
                        >
                          <Copy className="w-3 h-3" />
                          {snippetCopied === "js" ? "Copied!" : "Copy"}
                        </button>
                      </div>
                      <img
                        src="/js_logo.png"
                        alt="JavaScript logo"
                        className="h-12 object-contain rounded-lg mx-auto mt-2"
                      />
                      {/* <pre className="text-[10px] whitespace-pre-wrap bg-[#050505] border border-[#222] rounded px-2 py-1 overflow-x-auto">
                        {PLAYSET_READY_SNIPPETS.js}
                      </pre> */}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {error && (
              <div className="text-xs text-red-400 bg-red-900/30 border border-red-700 rounded-lg px-3 py-2">
                {error}
              </div>
            )}

            {!game && (
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center px-4 py-2 rounded-lg border border-[#8338ec] bg-[#8338ec]/20 hover:bg-[#8338ec] text-sm text-white transition disabled:opacity-60"
              >
                {saving ? "Creating…" : "Create game"}
              </button>
            )}

            {game && (
              <div className="text-xs text-green-300">
                Game created. Next, upload your first build.
              </div>
            )}
          </form>
        </section>

        {/* Step 2: Upload initial build */}
        <section className="bg-[#151515] border border-[#252525] rounded-xl p-5 space-y-4">
          <h2 className="text-lg font-semibold">Step 2 — Upload first build</h2>
          {!game ? (
            <p className="text-xs text-[#aaa]">
              Create the game first, then you&apos;ll be able to upload a build.
            </p>
          ) : (
            <>
              <p className="text-xs text-[#aaa]">
                This build will become the current build for{" "}
                <span className="font-medium">{game.name}</span>.
              </p>
              <UploadBuild
                gameId={game.id}
                onUploaded={handleInitialBuildUploaded}
              />
              <button
                type="button"
                onClick={() => navigate(`/dashboard?game=${game.id}`)}
                className="mt-4 inline-flex items-center px-4 py-2 rounded-lg border border-[#444] text-sm text-[#ddd] hover:border-[#38bdf8] hover:text-[#38bdf8] transition"
              >
                Skip for now · Go to dashboard
              </button>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
